import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useUpdateIndicativeAmountForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { useUpdateIndicativeAmountForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { getGetDealOpportunityLimitedPartnerViewV3QueryKey } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { formatMoney } from "@vega-core/utils/formatMoney";
import { trackCoInvestIoiSubmitted } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseIndicateInterestProps = {
  dealOpportunityId: string;
  initialMoney?: CoreMoney;
  dealId?: string;
  isGpContext?: boolean;
};

export const useIndicateInterest = ({
  dealOpportunityId,
  initialMoney,
  dealId,
  isGpContext = false,
}: UseIndicateInterestProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const modal = useBoolean(false);

  // LP hook
  const { mutate: updateIndicativeAmountLp, isPending: isSubmittingLp } =
    useUpdateIndicativeAmountForDealOpportunity({
      mutation: {
        onSuccess: (_, variables) => {
          const formattedAmount = formatMoney({
            amount: variables.data.indicationOfInterestAmount.amount,
            currency: variables.data.indicationOfInterestAmount.currency,
          });

          showToast({
            variant: "success",
            title: m.status_success(),
            description: m.coInvestments_indicateInterest_successMessage({
              amount: formattedAmount,
            }),
          });

          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityLimitedPartnerViewV3QueryKey(
                dealOpportunityId,
              ),
          });
          modal.setFalse();

          trackCoInvestIoiSubmitted({
            dealOpportunityId,
            dealId,
            outcome: "succeeded",
          });
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_indicateInterest_errorMessage(),
          });
        },
      },
    });

  // GP hook
  const { mutate: updateIndicativeAmountGp, isPending: isSubmittingGp } =
    useUpdateIndicativeAmountForDealOpportunityGp({
      mutation: {
        onSuccess: (_, variables) => {
          const formattedAmount = formatMoney({
            amount: variables.data.indicationOfInterestAmount.amount,
            currency: variables.data.indicationOfInterestAmount.currency,
          });

          showToast({
            variant: "success",
            title: m.status_success(),
            description: m.coInvestments_indicateInterest_successMessage({
              amount: formattedAmount,
            }),
          });

          if (dealId) {
            queryClient.invalidateQueries({
              queryKey: getGetDealTrackingForGpQueryKey(dealId),
            });
          }

          modal.setFalse();
        },
        onError: (error: ErrorType<unknown>) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_indicateInterest_errorMessage(),
          });
        },
      },
    });

  const updateIndicativeAmount = isGpContext
    ? updateIndicativeAmountGp
    : updateIndicativeAmountLp;
  const isSubmitting = isGpContext ? isSubmittingGp : isSubmittingLp;

  const handleIndicateInterest = useCallback(
    (amount: number, currency: CoreCurrency) => {
      if (!isGpContext) {
        trackCoInvestIoiSubmitted({
          dealOpportunityId,
          dealId,
          outcome: "attempted",
        });
      }

      updateIndicativeAmount({
        dealOpportunityId,
        data: {
          indicationOfInterestAmount: {
            amount,
            currency,
          },
        },
      });
    },
    [dealOpportunityId, dealId, isGpContext, updateIndicativeAmount],
  );

  const handleOpenModal = useCallback(() => {
    modal.setTrue();
  }, [modal]);

  const handleCloseModal = useCallback(() => {
    if (!isSubmitting) {
      modal.setFalse();
    }
  }, [isSubmitting, modal]);

  return {
    modal: {
      isOpen: modal.isOpen,
      onOpen: handleOpenModal,
      onClose: handleCloseModal,
    },
    handleIndicateInterest,
    isSubmitting,
    initialMoney,
  };
};
